import { useEffect, useMemo, useState } from 'react';
import { getCharacterStats, updateCharacter, type Character, type CharacterStats, type GameTile } from '../../lib/api';
import { useEscapeClose } from '../../lib/useEscapeClose';
import { IconAvatar } from './IconAvatar';
import { IconPicker, type LockedCosmeticInfo } from './IconPicker';
import { SeatPreview } from './SeatPreview';
import { EmbersShop } from '../me/EmbersShop';
import { CollapseSection } from '../me/CollapseSection';
import { titleRarityClass } from '../me/titleRarity';
import { Gamepad2, Sparkles, Trophy } from 'lucide-react';
import {
  DEFAULT_SEAT_COSMETICS,
  SEAT_BACKGROUNDS,
  SEAT_BG_HUES,
  SEAT_BORDERS,
  SEAT_PARTICLES,
  type SeatCosmetics,
} from '@tt/lobby';

interface Props {
  charId: string | null;
  games: GameTile[];
  editable?: boolean;
  onClose: () => void;
  onUpdated?: (character: Character) => void;
}

function findGameTile(games: GameTile[], gameId: string): GameTile | undefined {
  return games.find((g) => g.id === gameId) ?? games.find((g) => g.baseGameId === gameId);
}

export function CharacterProfileModal({ charId, games, editable = false, onClose, onUpdated }: Props) {
  const [stats, setStats] = useState<CharacterStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [icon, setIcon] = useState<Character['icon'] | null>(null);
  const [favoriteGameId, setFavoriteGameId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [seatCosmetics, setSeatCosmetics] = useState<SeatCosmetics>(DEFAULT_SEAT_COSMETICS);
  const [error, setError] = useState<string | null>(null);
  useEscapeClose(charId !== null, onClose);
  const [inspect, setInspect] = useState<InspectInfo | null>(null);

  useEffect(() => {
    if (!charId) return;
    setStats(null);
    setEditing(false);
    setError(null);
    void (async () => {
      try {
        const next = await getCharacterStats(charId);
        setStats(next);
        setIcon(next.icon);
        setFavoriteGameId(next.favoriteGameId ?? null);
        setSelectedTitle(next.selectedTitle ?? null);
        setSeatCosmetics(normalizeSeat(next.seatCosmetics));
      } catch {
        setError('Could not load profile.');
      }
    })();
  }, [charId]);

  const favorite = useMemo(() => {
    if (!stats) return null;
    const explicit = stats.favoriteGameId ? findGameTile(games, stats.favoriteGameId) : null;
    if (explicit) return explicit;
    const best = Object.entries(stats.perGame).sort((a, b) => b[1].matchesPlayed - a[1].matchesPlayed)[0];
    return best ? findGameTile(games, best[0]) ?? null : null;
  }, [games, stats]);

  if (!charId) return null;

  async function save() {
    if (!stats || !icon || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateCharacter(stats.charId, { icon, favoriteGameId, selectedTitle, seatCosmetics });
      onUpdated?.(updated);
      const refreshed = await getCharacterStats(stats.charId);
      setStats(refreshed);
      setIcon(refreshed.icon);
      setFavoriteGameId(refreshed.favoriteGameId ?? null);
      setSelectedTitle(refreshed.selectedTitle ?? null);
      setSeatCosmetics(normalizeSeat(refreshed.seatCosmetics));
      setEditing(false);
    } catch {
      setError('Could not save profile. Some choices may still be locked.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profile-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <section className="profile-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="profile-header profile-header-v2">
          <div className="profile-identity">
            {stats && <IconAvatar icon={editing && icon ? icon : stats.icon} size={96} alt={stats.name} />}
            <div className="profile-identity-text">
              <h2 className="display">{stats?.name ?? 'Loading...'}</h2>
              <p className={`title-pill ${titleRarityClass(selectedTitle ?? stats?.selectedTitle ?? null)}`}>{selectedTitle ?? stats?.selectedTitle ?? 'Tavern Regular'}</p>
              {stats && (
                <div className="profile-level-row" aria-label="Level + season progress">
                  <span className="profile-level-chip">Lv {stats.accountLevel ?? 1}</span>
                  {stats.season && (
                    <>
                      <span className="profile-level-bar" aria-hidden="true">
                        <span style={{ width: `${Math.min(100, ((stats.season.toNext.current) / Math.max(1, stats.season.toNext.needed)) * 100)}%` }} />
                      </span>
                      <small>S{stats.season.season} · T{stats.season.tier} · {stats.season.toNext.current}/{stats.season.toNext.needed} SXP</small>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <button type="button" className="link" onClick={onClose}>close</button>
        </header>

        {error && <div role="alert" className="gate-error">{error}</div>}

        {stats && (
          <>
            <div className="profile-stat-grid">
              <ProfileStat label="Points" value={stats.totalPoints} />
              <ProfileStat label="Rounds" value={stats.matchesPlayed} />
              <ProfileStat label="Round wins" value={stats.roundWins} />
              <ProfileStat label="Set wins" value={stats.setWins} />
              <ProfileStat label="Awards" value={stats.mvpAwards} />
              <ProfileStat label="Favorite" value={favorite?.name ?? 'None'} />
              <ProfileStat label="Daily streak" value={`${stats.dailyStreak ?? 0}🔥`} />
              <ProfileStat label="Best streak" value={stats.bestDailyStreak ?? 0} />
            </div>

            {editing ? (
              <div className="profile-edit">
                <section className="profile-section profile-presence-editor">
                  <h3>Lobby presence</h3>
                  <p className="muted" style={{ margin: '0 0 0.5rem' }}>
                    This is exactly how your tile will look in the lobby. Hat, body,
                    face, hue, title flair, seat frame, backdrop, and particles all
                    render from here.
                  </p>
                  {icon && (
                    <SeatPreview
                      icon={icon}
                      seatCosmetics={seatCosmetics}
                      selectedTitle={selectedTitle ?? null}
                      name={stats.name}
                      avatarSize={64}
                    />
                  )}
                </section>
                {icon && (
                  <IconPicker
                    value={icon}
                    onChange={setIcon}
                    inventory={stats.inventory}
                    onInspectLocked={(item) => setInspect(inspectFromUnlock(item))}
                  />
                )}
                <label className="profile-field">
                  <span>Favorite game</span>
                  <select className="setting-input" value={favoriteGameId ?? ''} onChange={(e) => setFavoriteGameId(e.target.value || null)}>
                    <option value="">Auto</option>
                    {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </label>
                <label className="profile-field">
                  <span>Title</span>
                  <select className="setting-input" value={selectedTitle ?? 'Tavern Regular'} onChange={(e) => setSelectedTitle(e.target.value)}>
                    {stats.inventory.titles.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <PresenceEditor
                  value={seatCosmetics}
                  inventory={stats.inventory}
                  onChange={setSeatCosmetics}
                  onInspect={(item) => setInspect(inspectFromUnlock(item))}
                />
                {inspect && <InspectPanel info={inspect} onClose={() => setInspect(null)} />}
                <EmbersShop
                  charId={stats.charId}
                  stats={stats}
                  onPurchase={() => {
                    void getCharacterStats(stats.charId).then((next) => {
                      setStats(next);
                      setIcon(next.icon);
                      setSelectedTitle(next.selectedTitle ?? null);
                      setFavoriteGameId(next.favoriteGameId ?? null);
                      setSeatCosmetics(normalizeSeat(next.seatCosmetics));
                    }).catch(() => {});
                  }}
                />

                <div className="profile-actions">
                  <button type="button" className="btn ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
                  <button type="button" className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</button>
                </div>
              </div>
            ) : (
              <>
                {(() => {
                  // Use mastery (always 16 entries from server-side
                  // MASTERY_GAMES_C) as the canonical game list. Falls
                  // back to perGame keys / games prop if mastery isn't
                  // populated yet for some reason.
                  const masteryRows = stats.inventory.mastery ?? [];
                  const idToName = new Map<string, string>();
                  for (const m of masteryRows) idToName.set(m.gameId, m.gameName);
                  for (const g of games) idToName.set(g.id, g.name);
                  for (const id of Object.keys(stats.perGame)) if (!idToName.has(id)) idToName.set(id, id);
                  const gameRows = Array.from(idToName.entries()).map(([id, name]) => {
                    const row = stats.perGame[id];
                    return {
                      id,
                      name,
                      matchesPlayed: row?.matchesPlayed ?? 0,
                      roundWins: row?.roundWins ?? row?.wins ?? 0,
                      bestScore: row?.bestScore ?? 0,
                    };
                  }).sort((a, b) => b.matchesPlayed - a.matchesPlayed || a.name.localeCompare(b.name));
                  const playedCount = gameRows.filter((r) => r.matchesPlayed > 0).length;
                  return (
                    <CollapseSection
                      id="profile-games"
                      icon={<Gamepad2 size={16} />}
                      title="Game stats"
                      summary={`${playedCount} / ${gameRows.length} games tried`}
                      defaultOpen={false}
                    >
                      <div className="profile-scroll profile-game-list">
                        {gameRows.map((row) => (
                          <div key={row.id} className={`profile-game-row${row.matchesPlayed === 0 ? ' unplayed' : ''}`}>
                            <strong>{row.name}</strong>
                            <span>{row.matchesPlayed} played</span>
                            <span>{row.roundWins} wins</span>
                            <span>best {row.bestScore}</span>
                          </div>
                        ))}
                        {gameRows.length === 0 && <p className="muted">No games loaded yet.</p>}
                      </div>
                    </CollapseSection>
                  );
                })()}

                {stats.inventory.mastery && stats.inventory.mastery.length > 0 && (
                  <CollapseSection
                    id="profile-mastery"
                    icon={<Sparkles size={16} />}
                    title="Game mastery"
                    summary={(() => {
                      const masters = stats.inventory.mastery!.filter((m) => m.tier === 3).length;
                      return `${masters} master${masters === 1 ? '' : 's'} earned`;
                    })()}
                    defaultOpen={false}
                  >
                    <div className="profile-scroll mastery-grid">
                      {stats.inventory.mastery.map((m) => (
                        <div key={m.gameId} className={`mastery-row tier-${m.tier}`}>
                          <span className="mastery-name">{m.gameName}</span>
                          <span className="mastery-tier" aria-label={`tier ${m.tier}`}>
                            {m.tier === 0 ? '○○○' : m.tier === 1 ? '●○○' : m.tier === 2 ? '●●○' : '●●●'}
                          </span>
                          <span className="mastery-progress muted">
                            {m.played} played · {m.wins} won
                          </span>
                          {m.nextHint && <span className="mastery-next muted">→ {m.nextLabel}: {m.nextHint}</span>}
                        </div>
                      ))}
                    </div>
                  </CollapseSection>
                )}

                <CollapseSection
                  id="profile-badges"
                  icon={<Trophy size={16} />}
                  title="Badges & unlocks"
                  summary={`${stats.achievements.length} badge${stats.achievements.length === 1 ? '' : 's'}`}
                  defaultOpen={false}
                >
                  <div className="profile-badge-row">
                    {stats.achievements.map((a) => <span key={a.code} className="award-chip">{a.label}</span>)}
                    {stats.achievements.length === 0 && <span className="muted">No badges yet.</span>}
                  </div>
                  <UnlockList hints={stats.inventory.unlockHints ?? []} onInspect={(item) => setInspect(inspectFromUnlock(item))} />
                  {inspect && <InspectPanel info={inspect} onClose={() => setInspect(null)} />}
                </CollapseSection>

                {editable && (
                  <div className="profile-actions">
                    <button type="button" className="btn primary" onClick={() => setEditing(true)}>Customize</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function ProfileStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="profile-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

const UNLOCK_GROUPS: Array<{ key: 'body' | 'hat' | 'face' | 'hue' | 'accentHue' | 'title' | 'emote' | 'seatBorder' | 'seatBackground' | 'seatParticle' | 'seatBgHue'; label: string }> = [
  { key: 'title', label: 'Titles' },
  { key: 'seatBorder', label: 'Seat borders' },
  { key: 'seatBackground', label: 'Seat backgrounds' },
  { key: 'seatParticle', label: 'Seat particles' },
  { key: 'seatBgHue', label: 'Seat background hues' },
  { key: 'body', label: 'Bodies' },
  { key: 'hat', label: 'Hats' },
  { key: 'face', label: 'Faces' },
  { key: 'hue', label: 'Colors' },
  { key: 'emote', label: 'Emotes' },
];

interface InspectInfo {
  kind: string;
  title: string;
  body: string;
  action: string;
}

function UnlockList({ hints, onInspect }: { hints: NonNullable<import('../../lib/api').CosmeticInventory['unlockHints']>; onInspect: (item: LockedCosmeticInfo) => void }) {
  const [showAll, setShowAll] = useState(false);
  if (hints.length === 0) return null;
  const grouped = UNLOCK_GROUPS.map((g) => ({
    ...g,
    items: hints.filter((h) => h.kind === g.key),
  })).filter((g) => g.items.length > 0);
  const total = hints.length;
  const unlockedCount = hints.filter((h) => h.unlocked).length;
  return (
    <div className="unlock-progression">
      <div className="unlock-progress-bar" aria-label={`${unlockedCount} of ${total} unlocked`}>
        <div className="unlock-progress-fill" style={{ width: `${(unlockedCount / Math.max(1, total)) * 100}%` }} />
        <span className="unlock-progress-label">{unlockedCount} / {total} unlocked</span>
      </div>
      {grouped.map((g) => {
        const visible = showAll ? g.items : g.items.slice(0, 6);
        return (
          <div key={g.key} className="unlock-group">
            <h4 className="unlock-group-title">{g.label}</h4>
            <div className="unlock-list">
              {visible.map((u) => (
                <button
                  key={`${u.kind}-${u.id}`}
                  type="button"
                  className={`unlock-chip${u.unlocked ? ' unlocked' : ''}`}
                  title={u.hint}
                  onClick={() => onInspect(u)}
                >
                  {u.unlocked ? `✓ ${u.label}` : formatUnlockHint(u)}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {!showAll && total > grouped.reduce((s, g) => s + Math.min(g.items.length, 6), 0) && (
        <button type="button" className="link" onClick={() => setShowAll(true)}>Show all unlocks</button>
      )}
    </div>
  );
}

function InspectPanel({ info, onClose }: { info: InspectInfo; onClose: () => void }) {
  return (
    <div className="profile-inspect-panel" aria-live="polite">
      <div>
        <span>{info.kind}</span>
        <button type="button" className="link" onClick={onClose}>close</button>
      </div>
      <strong>{info.title}</strong>
      <p>{info.body}</p>
      <small>{info.action}</small>
    </div>
  );
}

function PresenceEditor({
  value,
  inventory,
  onChange,
  onInspect,
}: {
  value: SeatCosmetics;
  inventory: CharacterStats['inventory'];
  onChange: (next: SeatCosmetics) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
}) {
  return (
    <section className="profile-section presence-locked-section">
      <h3>Seat cosmetics</h3>
      <PresenceGrid
        title="Borders"
        kind="seatBorder"
        selected={value.border}
        items={SEAT_BORDERS}
        unlocked={inventory.seatBorders ?? ['none']}
        onSelect={(border) => onChange({ ...value, border })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <PresenceGrid
        title="Backgrounds"
        kind="seatBackground"
        selected={value.background}
        items={SEAT_BACKGROUNDS}
        unlocked={inventory.seatBackgrounds ?? ['plain', 'tavern']}
        onSelect={(background) => onChange({ ...value, background })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <PresenceGrid
        title="Particles"
        kind="seatParticle"
        selected={value.particle}
        items={SEAT_PARTICLES}
        unlocked={inventory.seatParticles ?? ['none']}
        onSelect={(particle) => onChange({ ...value, particle })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <SeatBgHueGrid
        selected={value.bgHue ?? null}
        unlocked={inventory.seatBgHues ?? [25, 210, 290]}
        hints={inventory.unlockHints ?? []}
        onSelect={(bgHue) => onChange({ ...value, bgHue })}
        onInspect={onInspect}
      />
    </section>
  );
}

function SeatBgHueGrid({
  selected,
  unlocked,
  hints,
  onSelect,
  onInspect,
}: {
  selected: number | null;
  unlocked: number[];
  hints: NonNullable<CharacterStats['inventory']['unlockHints']>;
  onSelect: (next: number | null) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
}) {
  return (
    <div className="presence-category">
      <h4 className="unlock-group-title">Background hue</h4>
      <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.78rem' }}>
        Pick a custom gradient color for your tile. "Off" falls back to the
        themed background you picked above.
      </p>
      <div className="hue-row">
        <button
          type="button"
          className={`hue-cell hue-cell-off${selected === null ? ' selected' : ''}`}
          onClick={() => onSelect(null)}
          aria-label="No custom hue (use preset background)"
          title="Off — use the themed background above"
        >
          <span style={{ fontSize: '0.7rem' }}>off</span>
        </button>
        {SEAT_BG_HUES.map((h) => {
          const isUnlocked = unlocked.includes(h.id);
          const hint = hints.find((x) => x.kind === 'seatBgHue' && Number(x.id) === h.id);
          return (
            <button
              key={h.id}
              type="button"
              className={`hue-cell${selected === h.id ? ' selected' : ''}${isUnlocked ? '' : ' locked'}`}
              onClick={() => isUnlocked
                ? onSelect(h.id)
                : onInspect(hint ?? { kind: 'seatBgHue', id: h.id, label: h.label, hint: 'Unlock through play.' })}
              style={{ background: `hsl(${h.id} 70% 50%)` }}
              aria-label={isUnlocked ? `${h.label} (hue ${h.id})` : `${h.label} locked`}
              title={isUnlocked ? h.label : `${h.label} locked`}
            />
          );
        })}
      </div>
    </div>
  );
}

function PresenceGrid<T extends string>({
  title,
  kind,
  selected,
  items,
  unlocked,
  onSelect,
  onInspect,
  hints,
}: {
  title: string;
  kind: LockedCosmeticInfo['kind'];
  selected: T;
  items: Array<{ id: T; label: string; description: string }>;
  unlocked: string[];
  onSelect: (id: T) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
  hints: NonNullable<CharacterStats['inventory']['unlockHints']>;
}) {
  return (
    <div className="presence-category">
      <h4 className="unlock-group-title">{title}</h4>
      <div className="presence-locked-grid">
        {items.map((item) => {
          const isUnlocked = unlocked.includes(item.id);
          const hint = hints.find((h) => h.kind === kind && h.id === item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`presence-locked-card presence-tile-${item.id}${selected === item.id ? ' selected' : ''}${isUnlocked ? '' : ' locked'}`}
              onClick={() => isUnlocked ? onSelect(item.id) : onInspect(hint ?? { kind, id: item.id, label: item.label, hint: 'Unlock this through play.' })}
              title={isUnlocked ? item.description : `${item.label} locked - click to see how to unlock`}
            >
              <strong>{item.label}</strong>
              <span>{isUnlocked ? (selected === item.id ? 'Equipped' : 'Unlocked') : formatUnlockHint(hint ?? { hint: 'Locked.' })}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function inspectFromUnlock(item: LockedCosmeticInfo): InspectInfo {
  const info = rewardKindInfo(item.kind);
  return {
    kind: info.kind,
    title: item.label,
    body: info.body,
    action: `How to unlock: ${formatUnlockHint(item)}`,
  };
}

function rewardKindInfo(kind: LockedCosmeticInfo['kind']): { kind: string; body: string } {
  switch (kind) {
    case 'body':
      return { kind: 'Character body', body: 'A new avatar body shape. Equip it in Customize once unlocked.' };
    case 'hat':
      return { kind: 'Hat cosmetic', body: 'A head accessory for your avatar. Other players see it on your character.' };
    case 'face':
      return { kind: 'Face cosmetic', body: 'A new avatar expression. It changes your look, not gameplay.' };
    case 'hue':
      return { kind: 'Body color', body: 'A main avatar color. Use it to change your character palette.' };
    case 'accentHue':
      return { kind: 'Accent color', body: 'A secondary color for hats, blush, and small character details.' };
    case 'title':
      return { kind: 'Title', body: 'A selectable nameplate title. It appears on your profile and under your name in the lobby.' };
    case 'emote':
      return { kind: 'Reaction emote', body: 'An extra reaction for the in-game reaction picker.' };
    case 'seatBorder':
      return { kind: 'Seat border', body: 'An animated frame around your lobby player tile. Other players see it in the room.' };
    case 'seatBackground':
      return { kind: 'Seat background', body: 'A backdrop behind your avatar on the lobby player tile.' };
    case 'seatParticle':
      return { kind: 'Seat particles', body: 'Small ambient effects around your lobby player tile.' };
    case 'seatBgHue':
      return { kind: 'Seat background hue', body: 'A custom gradient color for your lobby tile background. Overrides the themed backdrop above.' };
    default:
      return { kind: 'Reward', body: 'A reward for your character or account presence.' };
  }
}

function formatUnlockHint(hint: { hint: string; progress?: { current: number; target: number; label: string } }): string {
  if (!hint.progress) return hint.hint;
  const current = Math.min(hint.progress.current, hint.progress.target);
  return `${hint.hint} ${current}/${hint.progress.target} ${hint.progress.label}.`;
}

function normalizeSeat(value: CharacterStats['seatCosmetics']): SeatCosmetics {
  return {
    border: (value?.border as SeatCosmetics['border']) ?? DEFAULT_SEAT_COSMETICS.border,
    background: (value?.background as SeatCosmetics['background']) ?? DEFAULT_SEAT_COSMETICS.background,
    particle: (value?.particle as SeatCosmetics['particle']) ?? DEFAULT_SEAT_COSMETICS.particle,
    bgHue: (value as { bgHue?: number | null } | undefined)?.bgHue ?? null,
  };
}
